import { motion, useCycle } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Navigation from "./Navigation";
import { isMobile } from "react-device-detect";
import Content, { FacilityTopBar } from "./Content";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useNavigate } from "react-router-dom";
import {
  getCurrentSession,
  getVisibleFacilities,
  refreshSession,
} from "./utils/userManager";

const MainContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: flex-start;
`;

const NavigationPanel = styled(motion.div)`
  overflow-y: scroll;
  z-index: 2;
  color: black;
`;

const NoAccessMessage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #4a6283;
  font-size: 2.2vh;
  text-align: center;
  padding: 2vh;
`;

const sidebar = {
  open: () => ({
    width: "100%",
    transition: {
      type: "spring",
      stiffness: 20,
      damping: 10,
    },
  }),
  closed: {
    scale: 0,
    width: "0%",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 30,
    },
  },
};

const Status = () => {
  const [facilityNames, setFacilityNames] = useState([]);
  const [facilityLanes, setFacilityLanes] = useState([]);
  const [isOpen, toggleOpen] = useCycle(true, false);
  const [chosenFacility, setChosenFacility] = useState(null);
  const [loadingFacilities, setLoadingFacilities] = useState(true);
  const [facilityLoadError, setFacilityLoadError] = useState("");
  const navigate = useNavigate();
  const [session, setSession] = useState(() => getCurrentSession());

  const getFacilityLanes = useCallback(async (facility) => {
    if (!facility) {
      setFacilityLanes([]);
      return [];
    }

    try {
      const response = await fetch(
        `https://server.edab.online/getFacility?facility=${facility}`
      );
      const data = await response.json();
      setFacilityLanes(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch facility lanes:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        setFacilityLoadError("");
        const freshSession = await refreshSession();
        if (!freshSession) {
          setFacilityLoadError("Authentication failed. No active session was returned.");
          navigate("/");
          return;
        }

        setSession(freshSession);
        setLoadingFacilities(true);
        const response = await fetch("https://server.edab.online/getFacilityNames");
        if (!response.ok) {
          throw new Error(`Facility list request failed with status ${response.status}.`);
        }
        const allFacilities = await response.json();
        if (!Array.isArray(allFacilities)) {
          throw new Error("Facility list response was not an array.");
        }
        const allowedFacilities = getVisibleFacilities(allFacilities, freshSession);
        setFacilityNames(allowedFacilities);
      } catch (error) {
        console.error("Failed to fetch facility names:", error);
        setFacilityNames([]);
        setFacilityLoadError(error.message || "Failed to load facilities.");
      } finally {
        setLoadingFacilities(false);
      }
    };

    loadFacilities();
  }, [navigate]);

  useEffect(() => {
    if (facilityNames.length === 0) {
      setChosenFacility(null);
      setFacilityLanes([]);
      return;
    }

    if (!chosenFacility || !facilityNames.includes(chosenFacility)) {
      setChosenFacility(facilityNames[0]);
    }
  }, [chosenFacility, facilityNames]);

  useEffect(() => {
    if (!chosenFacility) {
      return;
    }

    getFacilityLanes(chosenFacility);
  }, [chosenFacility, getFacilityLanes]);

  const handleConstantRefresh = useCallback(async () => {
    if (!chosenFacility) {
      return;
    }
    await getFacilityLanes(chosenFacility);
  }, [getFacilityLanes, chosenFacility]);

  useInterval(handleConstantRefresh, 10000);

  function useInterval(callback, delay) {
    const savedCallback = useRef();

    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
      function tick() {
        savedCallback.current();
      }
      if (delay !== null) {
        let id = setInterval(tick, delay);
        return () => clearInterval(id);
      }
    }, [delay]);
  }

  const handleRefresh = useCallback(async () => {
    if (!chosenFacility) {
      return;
    }
    await getFacilityLanes(chosenFacility);
  }, [getFacilityLanes, chosenFacility]);

  function handleToggle() {
    toggleOpen();
  }

  function renderFacilityView(contentStyle = {}) {
    if (!chosenFacility) {
      return <NoAccessMessage>Select a facility.</NoAccessMessage>;
    }

    return (
      <Content
        chosenFacility={chosenFacility}
        iconSize={isMobile ? 1.5 : 2}
        isMobile={isMobile}
        handleToggle={isMobile ? handleToggle : undefined}
        style={contentStyle}
        itemIds={facilityLanes}
        showTopBar={false}
      />
    );
  }

  if (!session) {
    return null;
  }

  if (loadingFacilities) {
    return <NoAccessMessage>Loading facilities...</NoAccessMessage>;
  }

  if (facilityLoadError) {
    return <NoAccessMessage>{facilityLoadError}</NoAccessMessage>;
  }

  if (facilityNames.length === 0) {
    const isDevelopment = process.env.NODE_ENV === "development";
    return (
      <NoAccessMessage>
        <div>
          <div>No facilities were available for this account.</div>
          {isDevelopment && (
            <div style={{ marginTop: "1vh", fontSize: "1.6vh", lineHeight: 1.5 }}>
              <div>Session: {session?.name || "none"}</div>
              <div>Admin: {String(Boolean(session?.isAdmin))}</div>
              <div>
                Assigned facilities: {Array.isArray(session?.facilities) ? session.facilities.length : 0}
              </div>
              <div>
                Assigned names: {Array.isArray(session?.facilities) && session.facilities.length > 0
                  ? session.facilities.join(", ")
                  : "none"}
              </div>
            </div>
          )}
        </div>
      </NoAccessMessage>
    );
  }

  return isMobile ? (
    <div style={{ width: "100%", height: "100%" }}>
      <PullToRefresh onRefresh={handleRefresh}>
        {!isOpen && (
          <>
            <FacilityTopBar
              itemIds={facilityLanes}
              chosenFacility={chosenFacility}
              isMobile
              handleToggle={handleToggle}
              iconSize={1.5}
            />
            {renderFacilityView({
              width: "100%",
              height: "calc(100% - 5vh)",
            })}
          </>
        )}
        <NavigationPanel
          initial={false}
          animate={isOpen ? "open" : "closed"}
          custom={window.height}
          variants={sidebar}
        >
          <Navigation
            style={{
              width: "100%",
              position: "absolute",
              left: "0px",
            }}
            itemIds={facilityNames}
            onClick={(facility) => {
              if (isMobile) {
                toggleOpen();
              }
              setChosenFacility(facility);
            }}
          />
        </NavigationPanel>
      </PullToRefresh>
    </div>
  ) : (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <MainContainer>
        <Navigation
          chosen={chosenFacility || false}
          style={{
            width: "20vw",
          }}
          itemIds={facilityNames}
          onClick={(facility) => {
            setChosenFacility(facility);
          }}
        />
        <div style={{ height: "100%", width: "80vw", display: "flex", flexDirection: "column" }}>
          <FacilityTopBar
            itemIds={facilityLanes}
            chosenFacility={chosenFacility}
            iconSize={2}
          />
          {renderFacilityView({
            height: "calc(100% - 5vh)",
            width: "100%",
          })}
        </div>
      </MainContainer>
    </div>
  );
};

export default Status;
